<template>
    <BaseModal :title="organizationId ? 'Edit Organization' : 'Add Organization'" @close="closeModal" :visible="true">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-30">
                <div class="rlms-flex rlms-flex-col rlms-gap-15">
                    <div class="rlms-flex rlms-flex-col rlms-gap-10">
                        <label for="org_name" class="rlms-fw-500">Organization</label>
                        <input type="text" placeholder="Organization Name" id="org_name" maxlength="100"
                            v-model.trim="name" />
                        <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && nameError">{{
                            nameError
                        }}</span>
                    </div>
                    <div class="rlms-flex rlms-flex-col rlms-gap-10">
                        <div class="rlms-fw-500">Address</div>
                        <!-- Street Address -->
                        <div class="rlms-mb-2">
                            <input type="text" placeholder="Street Address" v-model.trim="address" id="address"
                                maxlength="50">
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && addressError">{{
                                    addressError }}</span>
                        </div>

                        <!-- Unit/Suite (Optional) -->
                        <div class="rlms-mb-2">
                            <input type="text" placeholder="Unit/Suite/Apt (optional)" v-model.trim="unit" id="org_unit"
                                maxlength="20">
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && unitError">{{
                                unitError }}</span>
                        </div>

                        <!-- City -->
                        <div class="rlms-mb-2">
                            <input type="text" placeholder="City" v-model.trim="city" id="org_city" maxlength="100">
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && cityError">{{
                                cityError }}</span>
                        </div>

                        <!-- Country -->
                        <div class="rlms-mb-2">
                            <select v-model.trim="country" id="org_country" class="rlms-select">
                                <option value="">Select Country</option>
                                <template v-for="(country, i) in countries" :key="i">
                                    <option :value="country.iso2">{{ country.name }}
                                    </option>
                                </template>
                                <!-- Add more countries as needed -->
                            </select>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && countryError">{{ countryError }}</span>
                        </div>

                        <!-- State/Province (conditional based on country) -->
                        <div class="rlms-mb-2" id="state_container" v-if="country">
                            <select v-model.trim="state" id="org_state" class="rlms-select">
                                <option value="">Select State/Province</option>
                                <template v-for="(statedata, i) in states" :key="i">
                                    <option :value="statedata.name">{{ statedata.name }}</option>
                                </template>
                            </select>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && stateError">{{
                                stateError }}</span>
                        </div>

                        <!-- Zip/Postal Code -->
                        <div class="rlms-mb-2">
                            <input type="text" placeholder="Postal/ZIP Code" v-model.trim="postal_code"
                                id="org_postal_code" maxlength="15">
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && postal_codeError">{{
                                    postal_codeError }}</span>
                        </div>
                    </div>
                    <!-- <div class="rlms-flex rlms-flex-col rlms-gap-10">
                        <div class="rlms-fw-500">Optional Settings</div>
                        <label class="rlms-us-none rlms-flex rlms-items-center rlms-gap-10">
                            <input type="checkbox" id="regressions" class="rlms-checkbox" v-model="regressions" />
                            <span>Allow Regressions</span>
                        </label>
                        <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                            v-if="submitted && regressionsError">{{
                                regressionsError }}</span>
                    </div> -->
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal" @click="closeModal">Cancel</button>
                <button class="rlms-btn rlms-btn-primary" id="save-org-btn" @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import { getCountries, getStates } from '@/utils/countries';
import axios from '@/services/axios';
import { showToast } from '@/utils/swal';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';

const loader = useLoaderStore();

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organization: {
        type: Object,
        default: null
    }
});

const countries = getCountries();
const states = ref([]);
const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);
const organizationId = ref(null);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    name: yup.string().required('Organization name is required.'),
    address: yup.string().required('Street address is required.'),
    unit: yup.string().optional(),
    city: yup.string().required('City is required.'),
    state: yup.string().optional(),
    postal_code: yup.string().required('Postal/ZIP code is required.'),
    country: yup.string().required('Country is required.'),
    regressions: yup.boolean(),
});

// Setup form with schema
const { handleSubmit, setErrors, setFieldValue } = useForm({
    validationSchema: formSchema,
    initialValues: {
        name: '',
        address: '',
        unit: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        regressions: true
    }
})

// Fields
const { value: name, errorMessage: nameError } = useField('name');
const { value: address, errorMessage: addressError } = useField('address');
const { value: unit, errorMessage: unitError } = useField('unit');
const { value: city, errorMessage: cityError } = useField('city');
const { value: state, errorMessage: stateError } = useField('state');
const { value: postal_code, errorMessage: postal_codeError } = useField('postal_code');
const { value: country, errorMessage: countryError } = useField('country');
// Regressions field - hidden but always set to true
useField('regressions');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();

            if (organizationId.value) {
                await axios.put(`/organizations/${organizationId.value}`, { ...values });
            } else {
                await axios.post('/organizations', { ...values });
            }

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: (organizationId.value) ? 'Organization updated successfully!' : 'Organization created successfully!',
            });

            emit('saved');
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};


watch(country, (newVal) => {
    states.value = getStates(newVal);
});

// Watch for prop change and populate form
watch(
    () => props.organization,
    (org) => {
        if (org) {
            organizationId.value = org.id;
            setFieldValue('name', org.name || '');
            setFieldValue('address', org.address || '');
            setFieldValue('unit', org.unit || '');
            setFieldValue('city', org.city || '');
            setFieldValue('state', org.state || '');
            setFieldValue('postal_code', org.postal_code || '');
            setFieldValue('country', org.country || '');
            setFieldValue('regressions', org?.regressions ? true : false);
        }
    },
    { immediate: true }
);

</script>
